/**
 * Auth Token Store
 * Stores the Auth0 token getter outside React context so it can be used
 * by React Router loaders (which run before components render).
 *
 * Also provides a mechanism for loaders to wait for auth to be ready.
 */

let tokenGetter: (() => Promise<string>) | null = null;
let authReadyResolver: (() => void) | null = null;
let authReadyPromise: Promise<void> | null = null;

function requireTokenGetter(): () => Promise<string> {
  if (!tokenGetter) {
    throw new Error("Auth token getter not registered after wait");
  }
  return tokenGetter;
}

// Create a promise that resolves when auth is ready
function getAuthReadyPromise(): Promise<void> {
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      authReadyResolver = resolve;
    });
  }
  return authReadyPromise;
}

/**
 * Register the Auth0 getAccessTokenSilently function for use by route loaders.
 * This should be called once when the auth provider initializes.
 */
export function registerTokenGetter(getter: () => Promise<string>): void {
  tokenGetter = getter;
  // Resolve any waiting promises
  if (authReadyResolver) {
    authReadyResolver();
  }
}

/**
 * Unregister the token getter (called on logout).
 */
export function unregisterTokenGetter(): void {
  tokenGetter = null;
  // Reset the auth ready promise for next auth cycle
  authReadyPromise = null;
  authReadyResolver = null;
}

/**
 * Get the current access token.
 * This can be called from outside React components (e.g., route loaders).
 *
 * If auth is not ready yet, this will wait up to 5 seconds for it.
 */
export async function getAccessToken(timeoutMs = 5000): Promise<string> {
  if (tokenGetter) {
    return tokenGetter();
  }

  // Wait for auth to be ready
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error("Auth timeout")), timeoutMs);
  });

  try {
    await Promise.race([getAuthReadyPromise(), timeoutPromise]);
    const getter = requireTokenGetter();
    return getter();
  } catch {
    throw new Error("Auth token getter not registered. Ensure AuthProvider is initialized.");
  }
}

/**
 * Check if a token getter has been registered.
 */
export function hasTokenGetter(): boolean {
  return tokenGetter !== null;
}
